import { useEffect, useState } from 'react';

import { App as CapApp } from '@capacitor/app';

import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import { appendAppLog } from '../../platform/appLog';
import { toRelayDateTime } from '../../persistence/relay';
import {
  beschreibeAbweichung,
  brauchtNeueMessung,
  ClockCheck,
  merkeZeitpruefung,
  setVersatzmelder,
  WARNSCHWELLE_SEKUNDEN,
} from '../../platform/clockCheck';
import { Dialog } from '../../ui/Dialog';
import styles from './Start.module.css';

/**
 * Meldet eine falsch gehende Geräteuhr.
 *
 * Erscheint beim Start und bei jeder Rückkehr in den Vordergrund — beides Momente, in denen
 * jemand die Uhr verstellt haben kann. Wegklickbar: es ist ein Hinweis, kein Riegel, und der
 * Prüfungsbetrieb darf daran nicht hängen.
 *
 * ⚠️ **Kein Netz heisst keine Meldung.** Ein Prüfungsnetz sperrt UDP 123 womöglich; dann
 * meldet das Plugin `available: false` und hier erscheint nichts. Eine Warnung „Zeit nicht
 * prüfbar" bei jedem Start wäre nach zwei Tagen unsichtbar und triebe die echte Warnung
 * gleich mit in die Blindheit.
 */
export function ClockWarning() {
  const t = useT();
  const [abweichung, setAbweichung] = useState<number | null>(null);
  /**
   * Der Zeitserver kommt aus den Einstellungen (`ntp_server`) und nicht mehr aus einer festen
   * Zeile hier — er gehört ins Manifest und muss benennbar sein, und ein Haus mit eigenem
   * Zeitserver soll ihn eintragen können. **Leer heisst: kein Abgleich.**
   */
  const ntpServer = useAppStore((s) => s.ntpServer);

  useEffect(() => {
    let aktuell = true;
    const server = ntpServer.trim();

    const prüfen = async () => {
      // Kein Server, keine Messung — und dann bleibt es bei `timeSource: 'system'`.
      if (!server) return;
      // Nicht bei jeder Rückkehr eine fremde Ressource fragen — s. `MINDESTABSTAND_MS`.
      if (!brauchtNeueMessung()) return;
      try {
        const ergebnis = await ClockCheck.offsetSeconds({ server });
        if (!aktuell || !ergebnis.available || ergebnis.offsetSeconds === undefined) return;

        // ⚠️ **Auch die unauffällige Prüfung wird gemerkt** — und gerade die. Eine Uhr, die
        // vor drei Minuten auf 0,4 Sekunden genau lag, ist die gute Nachricht, die jede
        // Beglaubigung ab jetzt mitträgt (s. `signing.ts::zeitherkunft`).
        merkeZeitpruefung({
          offsetSeconds: ergebnis.offsetSeconds,
          checkedAt: toRelayDateTime(new Date()),
          server,
        });

        if (Math.abs(ergebnis.offsetSeconds) < WARNSCHWELLE_SEKUNDEN) return;
        await appendAppLog(
          'Uhr weicht um ' + Math.round(ergebnis.offsetSeconds) + ' s von der Netzzeit ab.',
          'warn',
        );
        if (aktuell) setAbweichung(ergebnis.offsetSeconds);
      } catch {
        // Nicht erreichbar ist kein Fehler, sondern der Normalfall ohne Netz.
      }
    };

    void prüfen();

    /*
     * ⚠️ **Die zweite Quelle: der Server.** Bei jeder Anmeldung meldet Relay den Versatz
     * (`driftSeconds`); ab 30 s wird er protokolliert, ab 120 s landet er hier und wird
     * angezeigt (`platform/clockCheck.ts`). Der Unterschied zur Messung oben: Diese hier misst
     * gegen die **Serverzeit** — und genau danach ordnet Relay die Protokollzeilen.
     *
     * Der Rückruf steht im Effekt und nicht global, damit er mit dem Blatt verschwindet.
     */
    setVersatzmelder((sekunden) => {
      if (aktuell) setAbweichung(sekunden);
    });

    // Auch bei der Rückkehr in den Vordergrund: dazwischen kann die Uhr verstellt worden
    // sein, und genau dafür ist die Meldung da.
    const angemeldet =
      typeof window !== 'undefined'
        ? CapApp.addListener('appStateChange', (zustand) => {
            if (zustand.isActive) void prüfen();
          }).catch(() => null)
        : Promise.resolve(null);

    return () => {
      aktuell = false;
      setVersatzmelder(null);
      void angemeldet.then((h) => h?.remove());
    };
    // ⚠️ Der Effekt hängt am Server: trägt jemand in den Einstellungen einen anderen ein,
    // wird gegen den neuen gemessen. Eine Flut gibt das nicht — `brauchtNeueMessung` bremst.
  }, [ntpServer]);

  if (abweichung === null) return null;

  return (
    <Dialog
      kicker={t.settings.groupDevice}
      title={t.settings.clockOffTitle}
      onClose={() => setAbweichung(null)}
      width={460}
    >
      <p className={styles.resumeBody}>
        {t.settings.clockOffBody(
          beschreibeAbweichung(abweichung, {
            vor: t.clock.ahead,
            nach: t.clock.behind,
            stunde: t.clock.hour,
            stunden: t.clock.hours,
            minute: t.clock.minute,
            minuten: t.clock.minutes,
            sekunden: t.clock.seconds,
          }),
        )}
      </p>
      <p className={styles.resumeNote}>{t.settings.clockOffFix}</p>
      <div className={styles.resumeButtons}>
        <button
          type="button"
          className={`tap ${styles.primary}`}
          onClick={() => setAbweichung(null)}
        >
          {t.settings.clockOffClose}
        </button>
      </div>
    </Dialog>
  );
}
